import * as z from "zod";

// *** NOTE: importing from @shared/utils is not working ***
// *** so we are importing with relative path the functions here ***
// It create a circular dependency
import { isValidYear, dateExceedsOneYear } from "../../utils/dates";
import { getAsNumber } from "../../utils/get-as-number";
import { isEmpty } from "../../utils/is-empty";
import {
  GarageSizeEnum,
  SewageTypeEnum,
  SpaceUnitsEnum,
  TargetPropertyConstructionTypeEnum,
  UnitType,
  WaterTypeEnum,
  addressSchema,
  amountFrequencyOptional,
  rentalIncomeAmountFrequency,
  rentalIncomeAmountFrequencyOptional,
} from "../application";
import { amountSchema } from "../generic";

export const garageSchema = z
  .object({
    present: z.boolean({
      error: () => "form:error.required",
    }),
    sizeType: z
      .enum(GarageSizeEnum, {
        error: () => "form:error.required",
      })
      .optional()
      .nullable(),
    type: z.string().trim().min(2).optional().nullable(),
  })
  .superRefine((values, ctx) => {
    if (values.present) {
      const resSizeType = z
        .enum(GarageSizeEnum, {
          error: () => "form:error.required",
        })
        .safeParse(values.sizeType);
      const resType = z
        .string()
        .trim()
        .min(2, { message: "form:error.required" })
        .safeParse(values.type);

      if (!resSizeType.success) {
        resSizeType.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "form:error.required",
            path: ["sizeType", ...issue.path],
          });
        });
      }

      if (!resType.success) {
        resType.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "form:error.required",
            path: ["type", ...issue.path],
          });
        });
      }
    }
  });

export const optionalAmountSchema = z.preprocess(
  (v) => (isEmpty(v) ? null : getAsNumber(v)),
  z
    .number()
    .max(9_999_999, {
      message: "form:error.maxDollarAmount",
    })
    .nullable()
    .optional()
);

export const coOwnersheepFeesOptional = z
  .object({
    amount: z
      .preprocess(
        (v) => (isEmpty(v) ? null : getAsNumber(v)),
        z
          .number({
            error: (issue) => (issue.input === undefined ? "required" : "type"),
            coerce: false,
          })
          .max(9_999_999, {
            message: "form:error.maxDollarAmount",
          })
          .nullable()
      )
      .optional(),
    frequency: z.preprocess(
      (v) => (isEmpty(v) ? "" : v),
      z
        .enum([
          "WEEKLY",
          "BIWEEKLY",
          "MONTHLY",
          "SEMIMONTHLY",
          "SEMIANNUALLY",
          "ANNUALLY",
          "",
        ])
        .optional()
    ),
    heatingIncluded: z.boolean().nullable().optional(),
    ratio: z.number().nullable().optional(),
  })
  // make sure if one is filled, the other is filled
  .refine(
    (schema) => {
      // frequency is not empty and amount is null / empty field
      if (!isEmpty(schema.frequency) && isEmpty(schema.amount)) {
        return false;
      }

      return true;
    },
    {
      message: "form:error.missingAmount",
      path: ["amount"],
    }
  )
  .refine(
    (schema) => {
      // we want to support negative values for the amount and just check it is not 0
      return schema.amount && schema.amount !== 0 ? !!schema.frequency : true;
    },
    {
      message: "form:error.missingFrequency",
      path: ["frequency"],
    }
  );

export const amountUnitSchema = z.object({
  amount: z.preprocess(
    (v) => (isEmpty(v) ? null : getAsNumber(v)),
    z
      .number({
        error: (issue) =>
          issue.input === undefined ? "required" : "form:error.required",
        coerce: false,
      })
      .min(1, { message: "form:error.required" })
  ),
  unit: z.enum(SpaceUnitsEnum, {
    error: () => "form:error.required",
  }),
});

const coerceStringToBoolean = (v: unknown) =>
  v === "true"
    ? true
    : v === "false"
      ? false
      : v == null || v === ""
        ? undefined
        : v;

const requiredBooleanSchema = z.boolean({
  error: (issue) =>
    issue.input === undefined ? "form:error.required" : "form:error.invalid",
});

export const baseFoundPropertySchema = z.object({
  isFound: z.boolean(),
  isPropertyNew: z.preprocess(
    coerceStringToBoolean,
    z.boolean().nullable().optional()
  ),
  isRestrictedProperty: z.preprocess(
    coerceStringToBoolean,
    z.boolean().nullable().optional()
  ),
  numberOfUnits: z.preprocess(
    (v) => (isEmpty(v) ? null : getAsNumber(v)),
    z.number().min(1).max(4).optional().nullable()
  ),
  legalDescription: z.string().nullable().optional(),
  unitType: z.preprocess(
    (v) => (isEmpty(v) ? null : v),
    z.enum(UnitType).nullable().optional()
  ),
  ownerOccupied: z.boolean().nullable().optional(),
  rentalProperty: z.boolean().nullable().optional(),
  numberOfRentalsCurrentlyOwned: z.number().nullable().optional(),
  address: addressSchema,
  purpose: z.string().trim().min(2, { message: "form:error.required" }),
  acceptanceDate: z
    .string({
      error: () => "form:error.required",
    })
    .trim()
    .min(1, { message: "form:error.required" })
    .refine(
      (data) => {
        return dateExceedsOneYear(new Date())(data);
      },
      {
        message: "target-property:acceptanceDate.error",
      }
    ),
  purchasePrice: amountSchema,
  estimatedPropertyValue: amountSchema,
  schoolAndMunicipalTaxes: z.object({
    amount: amountSchema,
    year: z.preprocess(
      (v) => (isEmpty(v) ? null : getAsNumber(v)),
      z.number({
        error: () => "form:error.required",
      })
    ),
    paidOnYourBehalf: z.boolean().nullable().optional(),
  }),
  rentalIncome: rentalIncomeAmountFrequencyOptional?.nullable().optional(),
  propertyType: z.string().trim().min(2, { message: "form:error.required" }),
  propertyStyle: z.string().trim().min(2, { message: "form:error.required" }),
  constructionType: z.enum(TargetPropertyConstructionTypeEnum, {
    error: () => "form:error.required",
  }),
  yearBuilt: z.preprocess(
    (v) => (isEmpty(v) ? null : getAsNumber(v)),
    z.number({ error: () => "form:error.required" }).refine(
      (data) => {
        return isValidYear(data);
      },
      {
        message: "form:error.invalidYear",
      }
    )
  ),
  tenure: z.string().trim().min(2, { message: "form:error.required" }),
  livingSpace: amountUnitSchema,
  lotSize: amountUnitSchema,
  heatingType: z.string().trim().min(2, { message: "form:error.required" }),
  specificHeatingType: z.string().nullable().optional(),
  coOwnershipFees: coOwnersheepFeesOptional,
  heatingCost: amountFrequencyOptional?.nullable().optional(),
  heatingCostIncluded: z.boolean().nullable().optional(),
  waterType: z.enum(WaterTypeEnum, {
    error: () => "form:error.required",
  }),
  sewageType: z.enum(SewageTypeEnum, {
    error: () => "form:error.required",
  }),
  garage: garageSchema,
});

type TargetPropertySchemaFlags = {
  showOwnerOccupied?: boolean;
  showRentalProperty?: boolean;
  showLegalDescription?: boolean;
  showIsPropertyNew?: boolean;
  showIsARestrictedProperty?: boolean;
  showNumberOfUnits?: boolean;
  showHeatingFeesAmount?: boolean;
  showHeatingFeesFrequency?: boolean;
  showOtherHeatingType?: boolean;
};

export const extendSchema = ({
  showOwnerOccupied,
  showRentalProperty,
  showLegalDescription,
  showIsPropertyNew,
  showIsARestrictedProperty,
  showNumberOfUnits,
  showHeatingFeesAmount,
  showHeatingFeesFrequency,
  showOtherHeatingType,
}: TargetPropertySchemaFlags = {}) => {
  const extendedBase = baseFoundPropertySchema.extend({
    numberOfUnits: showNumberOfUnits
      ? z.preprocess(
          (v) => (isEmpty(v) ? undefined : getAsNumber(v)),
          z
            .number({ error: () => "form:error.required" })
            .min(1)
            .max(4)
        )
      : z.preprocess(
          (v) => (isEmpty(v) ? null : getAsNumber(v)),
          z.number().min(1).max(4).optional().nullable()
        ),
    isPropertyNew: showIsPropertyNew
      ? z.preprocess(coerceStringToBoolean, requiredBooleanSchema)
      : z.preprocess(coerceStringToBoolean, z.boolean().nullable().optional()),
    isRestrictedProperty: showIsARestrictedProperty
      ? z.preprocess(coerceStringToBoolean, requiredBooleanSchema)
      : z.preprocess(coerceStringToBoolean, z.boolean().nullable().optional()),
    legalDescription: showLegalDescription
      ? z
          .string({ error: () => "form:error.required" })
          .trim()
          .min(1, { message: "form:error.required" })
      : z.string().nullable().optional(),
    ownerOccupied: showOwnerOccupied
      ? requiredBooleanSchema
      : z.boolean().nullable().optional(),
    rentalProperty: showRentalProperty
      ? requiredBooleanSchema
      : z.boolean().nullable().optional(),
  });

  // This could have been a single flag since having different values for them doesn't make sense.
  // Possible future improvement.
  const showHeatingFees = showHeatingFeesAmount || showHeatingFeesFrequency;

  const isFoundExtended = extendedBase
    .extend({ isFound: z.literal(true) })
    .superRefine((data, ctx) => {
      const heatingNotIncluded = data.coOwnershipFees.heatingIncluded === false;
      if (showHeatingFees && heatingNotIncluded) {
        if (isEmpty(data.heatingCost?.amount)) {
          ctx.addIssue({
            code: "custom",
            message: "form:error.required",
            path: ["heatingCost", "amount"],
          });
        }
        if (isEmpty(data.heatingCost?.frequency)) {
          ctx.addIssue({
            code: "custom",
            message: "form:error.required",
            path: ["heatingCost", "frequency"],
          });
        }
      }
      if (
        showOtherHeatingType &&
        data.heatingType === "OTHER" &&
        isEmpty(data.specificHeatingType)
      ) {
        ctx.addIssue({
          code: "custom",
          message: "form:error.required",
          path: ["specificHeatingType"],
        });
      }
    });

  return z
    .discriminatedUnion("isFound", [isFoundExtended, isNotFoundProperty])
    .and(purposeUnion);
};

export const isFoundPropertySchema = baseFoundPropertySchema.extend({
  isFound: z.literal(true),
});

export type IsFoundProperty = z.infer<typeof isFoundPropertySchema>;

export const isNotFoundProperty = z
  .object({
    isFound: z.literal(false),
    address: z.object({
      stateCode: z
        .string({ error: () => "form:error.required" })
        .trim()
        .min(1, { message: "form:error.required" }),
      countryCode: z.string().trim(),
      streetNumber: z.string().trim().nullable().optional(),
      street: z.string().trim().nullable().optional(),
      unit: z.string().trim().optional(),
      city: z.string().trim().nullable().optional(),
      postalCode: z.string().trim().nullable().optional(),
    }),
  })
  .extend(
    baseFoundPropertySchema.pick({
      purchasePrice: true,
      propertyType: true,
      purpose: true,
      acceptanceDate: true,
      rentalIncome: true,
    }).shape
  );

export type IsNotFoundProperty = z.infer<typeof isNotFoundProperty>;

export const RENTAL_PURPOSES = new Set(["OWNER_OCCUPIED_AND_RENTAL", "RENTAL"]);

export const purposeUnion = z
  .object({
    purpose: z.string().trim().min(2, { message: "form:error.required" }),
    rentalIncome: rentalIncomeAmountFrequencyOptional?.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (!RENTAL_PURPOSES.has(data.purpose)) {
      return;
    }
    const parsed = rentalIncomeAmountFrequency.safeParse(data.rentalIncome);
    if (parsed.success) return;
    for (const issue of parsed.error.issues) {
      ctx.addIssue({
        code: "custom",
        message: issue.message,
        path: ["rentalIncome", ...issue.path],
      });
    }
  });

export const discriminatedSchema = z
  .discriminatedUnion("isFound", [isFoundPropertySchema, isNotFoundProperty])
  .and(purposeUnion);

export type TargetPropertyForm = z.infer<typeof discriminatedSchema>;
